import React, { forwardRef } from "react";
import { CashPaymentOption } from "./CashPaymentOption";
import { CardPaymentOption } from "./CardPaymentOption";
import { BankTransferOption } from "./BankTransferOption";
import { ChequePaymentOption } from "./ChequePaymentOption";
import { ChequeInput } from "../../Page/pos/common/CashSale/useCashSaleCheckout";
import { getPaymentMethodCode, PaymentMethod } from "../../Enums";

export interface PaymentOptionValue {
  amount?: number;
  note?: string;
  bank_account_id?: string;
  transaction_no?: string;
  reference_no?: string;
  cheques?: ChequeInput[];
  cheque_no?: string;
  bank_name?: string;
  cheque_date?: string;
}

interface PaymentOptionProps {
  paymentMethodCode: string;
  value?: PaymentOptionValue;
  onChange?: (val: PaymentOptionValue) => void;
  bankAccounts: any[];
}

export const formatPaymentDetails = (
  value: PaymentOptionValue,
  code: string,
): PaymentOptionValue => {
  const normalizedCode = code?.toLowerCase();
  const base: PaymentOptionValue = {
    note: value.note,
  };

  if (normalizedCode === getPaymentMethodCode(PaymentMethod.CASH)) {
    return {
      ...base,
      amount: value.amount ?? 0,
    };
  }

  if (normalizedCode === getPaymentMethodCode(PaymentMethod.CARD)) {
    return {
      ...base,
      amount: value.amount ?? 0,
      bank_account_id: value.bank_account_id || "",
      transaction_no: value.transaction_no || "",
    };
  }

  if (normalizedCode === getPaymentMethodCode(PaymentMethod.BANK_TRANSFER)) {
    return {
      ...base,
      amount: value.amount ?? 0,
      bank_account_id: value.bank_account_id || "",
      reference_no: value.reference_no || "",
    };
  }

  if (normalizedCode === getPaymentMethodCode(PaymentMethod.CHEQUE)) {
    const cheques = value.cheques || [];
    const totalChequeAmount = cheques.reduce(
      (sum, c) => sum + (c.amount || 0),
      0,
    );
    const firstCheque = cheques[0];
    return {
      ...base,
      amount: totalChequeAmount,
      cheques,
      cheque_no: firstCheque?.cheque_no || "",
      bank_name: firstCheque?.bank_name || "",
      cheque_date: firstCheque?.cheque_date || "",
      bank_account_id: firstCheque?.bank_account_id || "",
    };
  }

  return base;
};

export const PaymentOption = forwardRef<any, PaymentOptionProps>(
  (
    { paymentMethodCode, value = {}, onChange = () => {}, bankAccounts },
    ref,
  ) => {
    const normalizedCode = paymentMethodCode?.toLowerCase();

    const updateField = (field: keyof PaymentOptionValue, fieldValue: any) => {
      const updated = {
        ...value,
        [field]: fieldValue,
      };
      onChange(formatPaymentDetails(updated, normalizedCode));
    };

    if (normalizedCode === getPaymentMethodCode(PaymentMethod.CASH)) {
      return (
        <CashPaymentOption
          ref={ref}
          cashAmount={value.amount ?? 0}
          setCashAmount={(amount) => updateField("amount", amount)}
          note={value.note}
          setNote={(note) => updateField("note", note)}
        />
      );
    }

    if (normalizedCode === getPaymentMethodCode(PaymentMethod.CARD)) {
      return (
        <CardPaymentOption
          ref={ref}
          cardAmount={value.amount ?? 0}
          setCardAmount={(amount) => updateField("amount", amount)}
          cardBankAccountId={value.bank_account_id || ""}
          setCardBankAccountId={(id) => updateField("bank_account_id", id)}
          cardTxNo={value.transaction_no || ""}
          setCardTxNo={(txNo) => updateField("transaction_no", txNo)}
          bankAccounts={bankAccounts}
          paymentMode="card"
          note={value.note}
          setNote={(note) => updateField("note", note)}
        />
      );
    }

    if (normalizedCode === getPaymentMethodCode(PaymentMethod.BANK_TRANSFER)) {
      return (
        <BankTransferOption
          ref={ref}
          transferAmount={value.amount ?? 0}
          setTransferAmount={(amount) => updateField("amount", amount)}
          transferBankAccountId={value.bank_account_id || ""}
          setTransferBankAccountId={(id) => updateField("bank_account_id", id)}
          transferTxNo={value.reference_no || ""}
          setTransferTxNo={(txNo) => updateField("reference_no", txNo)}
          bankAccounts={bankAccounts}
          paymentMode="bank_transfer"
          note={value.note}
          setNote={(note) => updateField("note", note)}
        />
      );
    }

    if (normalizedCode === getPaymentMethodCode(PaymentMethod.CHEQUE)) {
      return (
        <ChequePaymentOption
          ref={ref}
          cheques={value.cheques || []}
          onChange={(cheques) => {
            const updated = {
              ...value,
              cheques,
            };
            onChange(formatPaymentDetails(updated, normalizedCode));
          }}
          bankAccounts={bankAccounts}
        />
      );
    }

    return null;
  },
);
